import * as React from 'react';
import { TFunction } from 'i18next';
import {
  CompletingEvent,
  CurrentPageChangedEvent,
  LoadChoicesFromServerEvent,
  UpdateQuestionCssClassesEvent,
  ValueChangedEvent
} from 'survey-core/typings/survey-events-api';
import { IProduct, IProductsResponse, ITaxRate } from '../types/products';
import { ItemValue, Model } from 'survey-core/survey.core';
import { debounce, isUndefined, omit, pick } from 'lodash';
import { handleFormCompletion } from './complete';
import { ISurveyInitialData } from '../types/interfaces';
import { updateLead } from './events';
import { SELF_SERVE_VALUE } from '../components/questionTypes/MuiAdditionalProducts';
import { TYPE as MuiDiscountType } from '../components/questionTypes/MuiDiscount';
import { TYPE as MuiSelectableSubscriptionsType } from '../components/questionTypes/MuiSelectableSubscriptions';
import { TYPE as MuiRadioGroupType } from '../components/questionTypes/MuiRadioGroup';
import { TYPE as MuiCheckboxType } from '../components/questionTypes/MuiCheckbox';
import { TYPE as MuiPrivacyConsentType } from '../components/questionTypes/MuiPrivacyConsent';
import { OperationType } from '../types/enums';
import { getPurchaseInfo } from './gtm';
import { checkFoundation } from './common';
import axios from 'axios';
import { OdataResponse } from '@pec/aion-ui-odata/types/odataResponse';

type FormState = {
  data: any;
  page: number;
};

const questionCssClassesMap = {
  [MuiDiscountType]: ' mui-discount',
  [MuiSelectableSubscriptionsType]: ' mui-selectable-subscriptions',
  [MuiRadioGroupType]: ' mui-radiogroup',
  [MuiCheckboxType]: ' mui-checkbox',
  [MuiPrivacyConsentType]: ' mui-privacy-consent'
};

const CHECKOUT_URL_KEY = 'survey:checkout_';
const STORED_DISCOUNT_DATA = 'survey:storedDiscountData_';
export const FORM_STATE_KEY = 'survey:formState_';
export const TRANSACTION_ID_KEY = 'survey:transactionID_';
const SCOPE_CHANGE_LEVEL = 'Scope Change';

const getSessionStorageKey = (survey: Model, key: string) => {
  const { operationType, organizationId } = survey.getVariable('info');

  return `${key}${organizationId}_${operationType}`;
};

export const onLoadChoicesFromServer = (taxRatesCacheRef: React.MutableRefObject<Record<string, ITaxRate | null>>) => (
  survey: Model,
  options: Omit<LoadChoicesFromServerEvent, 'serverResult'> & { serverResult: IProductsResponse }
) => {
  const { question, choices, serverResult } = options;
  if (question.name === 'subscriptionsPreview') {
    options.choices = choices.filter(choice => choice.originalItem.productType !== 'Services');
    return; // Use choices from 'subscriptionSelect' question
  }
  if (!survey.getVariable('isSubscriptionDisplayed')) {
    return;
  }
  if (choices.length > 0) {
    survey.setVariable('productsChoices', choices);
  }
  if (!isUndefined(serverResult.taxRate)) {
    survey.setVariable('taxRate', serverResult.taxRate);
  }

  setTimeout(() => {
    const { processedUrl } = question.choicesByUrl;

    // Cached response will not be returned in options.serverResult, store previously received taxRate results
    if (taxRatesCacheRef.current[processedUrl] !== undefined) {
      survey.setVariable('taxRate', taxRatesCacheRef.current[processedUrl]);
      survey.currentPage.setPropertyValue('forceUpdated', Date.now());
    } else if (!isUndefined(serverResult.taxRate)) {
      taxRatesCacheRef.current[processedUrl] = serverResult.taxRate;
    }

    if (choices.length === 0 && question.activeChoices.length > 0) {
      // Cached choices will not be returned in options.choices, read choices from question once it is rendered
      survey.setVariable('productsChoices', question.activeChoices);
    }
    applyProductsValues(survey);
  }, 0);
};

export const onSurveyComplete = (
  onFormStateChange: ({ isLoading, error }: { isLoading: boolean; error?: string }) => void,
  t: TFunction,
  formDataChangedRef: React.MutableRefObject<boolean>,
  setOpenScopeChangeDialog: (arg: boolean) => void
) => async (survey: Model, options: CompletingEvent) => {
  const { operationType } = survey.getVariable('info');

  if (operationType === OperationType.ScopeChange) {
    setOpenScopeChangeDialog(true);
    options.allow = false;
    return;
  }

  let errorMessage = '';

  options.allow = false;
  onFormStateChange({ isLoading: true });

  try {
    if (formDataChangedRef.current) {
      await updateLead(survey, survey.data, survey.activePage);
    }
    const { data } = await handleFormCompletion(survey);
    const key = getSessionStorageKey(survey, TRANSACTION_ID_KEY);
    const checkoutKey = getSessionStorageKey(survey, CHECKOUT_URL_KEY);

    try {
      const purchaseInfo = getPurchaseInfo(survey, data.transactionId);
      if (purchaseInfo) {
        sessionStorage.setItem('purchaseInfo', JSON.stringify(purchaseInfo));
      }
    } catch {}

    localStorage.setItem(key, JSON.stringify(data.transactionId));
    sessionStorage.setItem(checkoutKey, data.checkoutUrl);

    window.location.href = data.checkoutUrl;
  } catch (error) {
    errorMessage = t('subscriber:survey.unknownError', 'Unknown error, please try again later.');

    onFormStateChange({ error: errorMessage, isLoading: false });
  }
};

const getFormState = (survey: Model): FormState => {
  try {
    const key = getSessionStorageKey(survey, FORM_STATE_KEY);
    return JSON.parse(sessionStorage.getItem(key) || '{ "data": {}, "page": 0 }');
  } catch {
    return { data: {}, page: 0 };
  }
};

const saveFormState = (survey: Model, savePageOnly = false) => {
  try {
    const prevFormState = getFormState(survey);
    const subscriptionQuestion = survey.getQuestionByName('subscriptionSelect');
    const isOrderPage = survey.activePage.containsElement(subscriptionQuestion);
    const additionalProducts = isUndefined(survey.data.additionalProducts)
      ? prevFormState.data.additionalProducts
      : survey.data.additionalProducts;
    const dataSharingConsent = survey.data.dataSharingConsent;

    const item = {
      page: savePageOnly ? survey.currentPageNo : prevFormState.page,
      data: savePageOnly
        ? prevFormState.data
        : {
            ...omit(
              survey.data,
              isOrderPage
                ? ['total', 'totalExplanation']
                : ['subscriptionSelect', 'additionalProducts', 'total', 'totalExplanation']
            ),
            ...(isOrderPage
              ? { additionalProducts }
              : pick(prevFormState.data, ['subscriptionSelect', 'additionalProducts'])),
            ...(isOrderPage ? { dataSharingConsent } : pick(prevFormState.data, ['dataSharingConsent']))
          }
    };

    sessionStorage.setItem(getSessionStorageKey(survey, FORM_STATE_KEY), JSON.stringify(item));
  } catch {}
};
const saveFormStateDebounced = debounce(saveFormState, 500);

const applyProductsValues = (survey: Model) => {
  const formState = getFormState(survey);
  if (formState) {
    const { data } = formState;
    const subscription = survey.getQuestionByName('subscriptionSelect');
    const subscriptionChoices = subscription.visibleChoices || [];

    subscription.clearValue();
    if (subscriptionChoices.find((choice: ItemValue) => choice.value === data.subscriptionSelect)) {
      survey.setValue('subscriptionSelect', data.subscriptionSelect, '', false);
      survey.setValue('dataSharingConsent', data.dataSharingConsent, '', false);
      setTimeout(() => {
        // Additional product's visibility is dependent on subscriptionSelect
        const filteredProducts = survey.getVariable('filteredProducts') || {};
        if (
          (Array.isArray(filteredProducts.additionalProducts) &&
            filteredProducts.additionalProducts.includes(data.additionalProducts)) ||
          data.additionalProducts === SELF_SERVE_VALUE
        ) {
          survey.setValue('additionalProducts', data.additionalProducts, '', false);
        }
        const appliedCode = survey.getQuestionByName('discountCode').value;
        const discount = sessionStorage.getItem(getSessionStorageKey(survey, STORED_DISCOUNT_DATA));

        if (appliedCode && discount) {
          survey.setVariable('discount', JSON.parse(discount));
          survey.getQuestionByName('discountCode').setPropertyValue('appliedCode', appliedCode);
          survey.currentPage.setPropertyValue('forceUpdated', Date.now());
        }
      }, 0);
    }
  }
};

export const onCurrentPageChanged = (
  formDataChangedRef: React.MutableRefObject<boolean>,
  prevPageVisitedRef: React.MutableRefObject<boolean>
) => (survey: Model, options: CurrentPageChangedEvent) => {
  const subscriptionQuestion = survey.getQuestionByName('subscriptionSelect');
  const isOrderPage = survey.activePage.containsElement(subscriptionQuestion);

  survey.setVariable('isSubscriptionDisplayed', isOrderPage ? true : null);
  if (isOrderPage) {
    applyProductsValues(survey);
  }
  saveFormStateDebounced.cancel();
  saveFormState(survey, true);

  if (options.isGoingBackward) prevPageVisitedRef.current = true;
  if (options.isGoingForward) {
    if (prevPageVisitedRef.current && !formDataChangedRef.current) return;
    const formState = getFormState(survey);
    formDataChangedRef.current = false;
    prevPageVisitedRef.current = false;
    updateLead(survey, { ...survey.data, ...(formState.data || {}) }, options.oldCurrentPage);
  }
};

export const applyFormStateFromStorage = (survey: Model) => {
  const formState = getFormState(survey);
  if (formState) {
    const { page, data } = formState;
    survey.currentPageNo = page;
    survey.data = omit(data, ['subscriptionSelect', 'additionalProducts']);

    const subscriptionQuestion = survey.getQuestionByName('subscriptionSelect');
    const isOrderPage = survey.activePage.containsElement(subscriptionQuestion);

    if (isOrderPage) survey.setVariable('isSubscriptionDisplayed', true);
  }
};

export const onValueChanged = (formDataChangedRef: React.MutableRefObject<boolean>) => (
  survey: Model,
  options: ValueChangedEvent
) => {
  if (['total', 'totalExplanation'].includes(options.name)) return;
  const info = survey.getVariable('info');
  const { applicationType, assessmentType, operationType } = info;
  if (operationType !== OperationType.ScopeChange) {
    saveFormStateDebounced(survey);
  }
  const formState = getFormState(survey);

  if (options.name === 'subscriptionSelect') {
    const additionalProduct = survey.getQuestionByName('additionalProducts');
    const dataSharingConsent = survey.getQuestionByName('dataSharingConsent');
    additionalProduct && additionalProduct.clearValue();
    dataSharingConsent && dataSharingConsent.clearValue();
  }
  if (['applicationType', 'assessmentType'].includes(options.name) && operationType === OperationType.ScopeChange) {
    const applicationFromQuestion = survey.getQuestionByName('applicationType');
    const assessmentTypeFromQuestion = survey.getQuestionByName('assessmentType');
    const completeDisabled =
      applicationType === applicationFromQuestion.value && assessmentType === assessmentTypeFromQuestion.value;

    survey.setVariable('completeDisabled', completeDisabled);
    survey.currentPage.setPropertyValue('forceUpdated', Date.now());
  }
  if (
    ['totalEmployeeCount', 'applicationType', 'assessmentType'].includes(options.name) &&
    formState &&
    operationType !== OperationType.ScopeChange
  ) {
    const newItem = { ...formState, data: { ...formState.data, subscriptionSelect: null, additionalProducts: null } };
    sessionStorage.setItem(getSessionStorageKey(survey, FORM_STATE_KEY), JSON.stringify(newItem));
  }

  if (['subscriptionSelect', 'additionalProducts', 'dataSharingConsent'].includes(options.name) && !options.value)
    return;
  if (['totalEmployeeCount', 'applicationType', 'assessmentType'].includes(options.name) && survey.data.discountCode) {
    survey.setValue('discountCode', null, '', false);
  }
  formDataChangedRef.current = true;
};

const applyForm = async (savedData: FormState, survey: Model, isFoundation: boolean) => {
  const info = survey.getVariable('info');
  const { employeeCount, subcontractorEmployeeCount, applicationType, assessmentType, operationType } = info;
  if (Object.keys(savedData.data)?.length) {
    applyFormStateFromStorage(survey);
  } else {
    if (savedData.page !== 0) {
      applyFormStateFromStorage(survey);
    }
    const totalEmployees = survey.getQuestionByName('totalEmployeeCount');
    const subcontractor = survey.getQuestionByName('subcontractorEmployeeCount');
    const application = survey.getQuestionByName('applicationType');
    const assessment = survey.getQuestionByName('assessmentType');
    const shouldUpdateLead = operationType === OperationType.Upgrade && !isFoundation;
    if (totalEmployees && subcontractor) {
      totalEmployees.value = employeeCount;
      subcontractor.value = subcontractorEmployeeCount;
    }
    if (application && assessment) {
      application.value = applicationType;
      assessment.value = assessmentType;
    }
    try {
      if (shouldUpdateLead) await updateLead(survey, survey.data);
    } catch {}
  }
};

export const setSurveyInfo = (survey: Model, data: ISurveyInitialData) => {
  try {
    const isFoundation = checkFoundation(data.currentSubscription);
    const companyData = {
      ...omit(data, ['defaultOperatingCountry', 'employeeCount']),
      employeeCount: data.employeeCount.employeeCount,
      subcontractorEmployeeCount: data.employeeCount.subcontractorEmployeeCount,
      location: data.defaultOperatingCountry.isDefault && data.defaultOperatingCountry.iso2Code,
      expiryDate: data.expiryDate?.split('/').join('-'),
      companyType: isFoundation ? 'Contractor' : data.companyType
    };

    survey.setVariable('info', companyData);
    const info = survey.getVariable('info');
    const { employeeCount, operationType } = info;

    if (isFoundation) {
      survey.setValue('organizationType', 'Contractor');
    }

    if (operationType === OperationType.ScopeChange) {
      survey.setVariable('completeDisabled', true);
    }

    const savedData = getFormState(survey);

    survey.setVariable('initialEmployeeCount', employeeCount);

    applyForm(savedData, survey, isFoundation);
  } catch (err) {
    throw err;
  }
};

export const onUpdateQuestionCssClasses = (_: Model, options: UpdateQuestionCssClassesEvent) => {
  // According to the documentation this is the best way to override default CSS styles
  // https://surveyjs.io/form-library/documentation/api-reference/survey-data-model#onUpdateQuestionCssClasses
  const classes = options.cssClasses;
  classes.mainRoot += questionCssClassesMap[options.question.getType()];

  if (options.question.name === 'totalExplanation') {
    classes.mainRoot += ' total-price-explanation';
  }
};

export const confirmScopeChange = async (
  survey: Model,
  onFormStateChange: ({ isLoading, error }: { isLoading: boolean; error?: string }) => void,
  t: TFunction
) => {
  onFormStateChange({ isLoading: true });
  const info = survey.getVariable('info');

  try {
    const { data } = await axios.get<OdataResponse<IProduct[]>>(
      `/api/v2/products?feesIncluded=true&$filter=subscriptionTier eq '${SCOPE_CHANGE_LEVEL}'`
    );
    const product = data.value.find(res => res.name === SCOPE_CHANGE_LEVEL);

    if (product) {
      try {
        await updateLead(survey, survey.data, survey.activePage, { ids: [product.id], codes: [product.productCode] });
        window.location.href = `${window.location.origin}/organizations/${info.organizationId}/scope-change/success?operationType=${OperationType.ScopeChange}`;
      } catch (err) {
        onFormStateChange({
          error: t('subscriber:survey.unknownError', 'Unknown error, please try again later.'),
          isLoading: false
        });
      }
    }
  } catch (err) {
    onFormStateChange({
      error: t('subscriber:survey.unknownError', 'Unknown error, please try again later.'),
      isLoading: false
    });
  }
};
